import React from "react";
import styled from "@emotion/styled";
import {Box, Collapse} from "@mui/material";
import {observer} from "mobx-react";
import {useViewController} from "data/services/locator";
import {Bindings} from "data/constants/bindings";
import {BonusType} from "data/enums";
import {ILineUp} from "views/components/modals/modal_compare/tournament/tournament.controller";
import {Exist} from "views/components/exist/exist.component";
import IconButton from "@mui/material/IconButton";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {ILineupRowController} from "./lineup_row.controller";
import {useIsMobile} from "data/hooks";
import {getPlayerHeadshot} from "data/utils/helper";

const Wrapper = styled.div<{isOpen: boolean; isBench: boolean}>`
	background: ${({isOpen, isBench}) => (isOpen && !isBench ? "#F2F2F2" : "transparent")};
	border-radius: 4px;
	border-bottom: 1px solid #f2f2f2;
`;

const Container = styled.div`
	display: grid;
	grid-template-columns: 1fr min-content 1fr;
`;

const LineupRowPlayer = styled.div`
	display: grid;
	grid-template-columns: min-content 1fr;
	font-size: 12px;
	padding: 4px 4px 4px 2px;

	&.reverse {
		grid-template-columns: 1fr min-content;
		padding: 4px 2px 4px 4px;
	}

	@media (min-width: 768px) {
		grid-template-columns: min-content 1fr;
		padding: 10px 8px;

		&.reverse {
			padding: 10px 8px;
			grid-template-columns: 1fr min-content;
		}
	}
`;

const LineupRowPlayerStats = styled.div`
	//display: grid;
	//grid-template-columns: repeat(3, 30px);
	//grid-column-gap: 1px;
	width: 100%;
	line-height: 165%;
	display: flex;
	justify-content: flex-start;
	align-items: center;
	gap: 4px;

	span {
		font-weight: bold;
		color: #000;
		display: inline-flex;
		min-width: max-content;
	}

	em {
		display: inline-flex;
	}

	@media (min-width: 768px) {
		//grid-template-columns: repeat(3, 41px);
		//grid-column-gap: 10px;
		color: #757575;
		font-weight: 600;
	}
`;

const PlayerImage = styled(Box)`
	width: 44px;
	min-width: 44px;
	height: 44px;
	cursor: pointer;
	position: relative;

	img {
		background: #d6d4d2;
		display: block;
		width: 100%;
		border-radius: 50%;
		overflow: hidden;
	}

	@media (min-width: 768px) {
		width: 52px;
		height: 52px;
		min-width: 52px;
	}
`;

const BonusesRow = styled.div`
	display: grid;
	grid-template-columns: 43px 43px 1fr 43px 43px;
	gap: 8px;
	font-size: 12px;
	font-weight: 600;
	line-height: 135%;
	padding: 8px 0;
	color: #2b2b2b;

	&.head {
		border-bottom: 1px solid #000;
	}

	@media (min-width: 768px) {
		padding: 10px 0;
	}
`;
// const PlayerNameRow = styled(Box)`
// 	display: flex;
// `;
// const PlayerName = styled(Box)`
// 	display: flex;
// 	flex: 1;
// 	font-size: 12px;
// 	padding: 0 20px;
// `;

const CapIcon = styled(Box)`
	display: flex;
	width: 16px;
	height: 16px;
	justify-content: center;
	align-items: center;
	border-radius: 50%;
	color: #fff;
	background: #004c95;
	font-family: var(--fontFamilyBase);
	font-size: 12px;
	position: absolute;
	top: -4px;
	left: -4px;
	z-index: 9;

	@media (min-width: 768px) {
		width: 20px;
		height: 20px;
		font-size: 15px;
	}
`;

const bonusesData = {
	[BonusType.HoleDoubleEagle]: {
		key: "modal.compare.double_eagle",
		defVal: "DOUBLE EAGLE",
	},
	[BonusType.HoleEagle]: {
		key: "modal.compare.eagle",
		defVal: "EAGLE",
	},
	[BonusType.HoleBirdie]: {
		key: "modal.compare.birdie",
		defVal: "BIRDIE",
	},
	[BonusType.HolePar]: {
		key: "modal.compare.par",
		defVal: "PAR",
	},
	[BonusType.HoleBogey]: {
		key: "modal.compare.bogey",
		defVal: "BOGEY",
	},
	[BonusType.HoleDoubleBogey]: {
		key: "modal.compare.double_bogey",
		defVal: "DOUBLE BOGEY",
	},
	[BonusType.HoleRoundBonus]: {
		key: "modal.compare.round_bonus",
		defVal: "END OF ROUND",
	},
	[BonusType.HoleFedex]: {
		key: "modal.compare.fedex_bonus",
		defVal: "FEDEX",
	},
};

interface ILineupRow extends ILineUp {
	isBench?: boolean;
	getValue: <T>(value: T) => T | string;
	selectedRoundName: number;
}

export const LineupRow: React.FC<ILineupRow> = observer(
	({playerB, playerA, bonuses, getValue, selectedRoundName, isBench = false}) => {
		const {i18n, isOpen, toggleHandler} = useViewController<ILineupRowController>(
			Bindings.LineupRowController
		);

		const {isMobile} = useIsMobile("(max-width: 600px)");
		const posCopy = i18n.t("modal.compare.position_label", "POS:");
		const totCopy = i18n.t("modal.compare.tournament_label", "TOT:");
		const roundCopy = i18n.t("modal.compare.round_label", "R{{ roundNumber }}:", {
			roundNumber: selectedRoundName,
		});

		return (
			<Wrapper isOpen={isOpen} isBench={isBench}>
				<Container>
					<LineupRowPlayer>
						<div className="flex flex-col gap-1 items-end md:justify-center">
							<div className="w-full flex justify-start items-center gap-2">
								<div className="font-[400] text-[#000] text-[14px]">
									{playerA?.name}
								</div>
								{/*<Exist when={playerA?.isCaptain}>
									<div className="relative w-[18px] h-[18px]">
										<CapIcon className="relative top-0 left-0">C</CapIcon>
									</div>
								</Exist>*/}
							</div>
							<LineupRowPlayerStats>
								<div className="flex gap-1 w-full items-start">
									<em>{posCopy}</em>
									<span>{getValue(playerA?.feedPosition)}</span>
								</div>
								<Exist when={!isMobile}>
									<div>|</div>
									<div className="flex gap-1">
										<em>{totCopy}</em>
										<span>{getValue(playerA?.tournamentPts)}</span>
									</div>
									<div>|</div>

									<div className="flex gap-1">
										<em>{roundCopy}</em>
										<span>{getValue(playerA?.roundPts)}</span>
									</div>
								</Exist>
							</LineupRowPlayerStats>

							<Exist when={isMobile}>
								<LineupRowPlayerStats>
									<div className="flex gap-1">
										<em>{totCopy}</em>
										<span>{getValue(playerA?.tournamentPts)}</span>
									</div>
									<div>|</div>

									<div className="flex gap-1">
										<em>{roundCopy}</em>
										<span>{getValue(playerA?.roundPts)}</span>
									</div>
								</LineupRowPlayerStats>
							</Exist>

							{/*<Exist when={isMobile}>*/}
							{/*	<div className="font-semibold mr-0.5 truncate max-w-[90px]">*/}
							{/*		{playerA?.name}*/}
							{/*	</div>*/}
							{/*</Exist>*/}
						</div>

						<div className="flex items-center gap-1 justify-end min-[400px]:ml-4 md:min-w-[120px] md:ml-6">
							<PlayerImage>
								<Exist when={playerA?.isCaptain}>
									<CapIcon>C</CapIcon>
								</Exist>
								<img
									src={getPlayerHeadshot(playerA?.headshot ?? null)}
									alt="player icon"
								/>
							</PlayerImage>
						</div>
					</LineupRowPlayer>

					<div className="flex items-center">
						<IconButton
							onClick={toggleHandler}
							className={`p-0 transition-all transform ${isOpen ? "rotate-180" : ""}`}>
							<KeyboardArrowDownIcon />
						</IconButton>
					</div>

					<LineupRowPlayer className="reverse">
						<div className="flex items-center gap-1 justify-start min-[400px]:mr-4 md:min-w-[120px] md:mr-6">
							<PlayerImage>
								<Exist when={playerB?.isCaptain}>
									<CapIcon>C</CapIcon>
								</Exist>
								<img
									src={getPlayerHeadshot(playerB?.headshot ?? null)}
									alt="player icon"
								/>
							</PlayerImage>
						</div>

						<div className="flex flex-col items-end md:justify-center">
							<div className="w-full flex justify-end items-center gap-2">
								{/*<Exist when={playerB?.isCaptain}>
									<div className="relative w-[18px] h-[18px]">
										<CapIcon className="relative top-0 left-0">C</CapIcon>
									</div>
								</Exist>*/}
								<div className="font-[400] text-[#000] text-[14px]">
									{playerB?.name}
								</div>
							</div>
							<LineupRowPlayerStats className="reverse flex w-full justify-end">
								{/*<div>{getValue(playerB?.feedPosition)}</div>*/}
								{/*<div>{getValue(playerB?.tournamentPts)}</div>*/}
								{/*<div>{getValue(playerB?.roundPts)}</div>*/}
								<Exist when={isMobile}>
									<div className="flex gap-1 w-full items-end justify-end">
										<em>{posCopy}</em>
										<span>{getValue(playerB?.feedPosition)}</span>
									</div>
								</Exist>
								<Exist when={!isMobile}>
									<div className="flex gap-1 w-full items-end justify-end">
										<em>{roundCopy}</em>
										<span>{getValue(playerB?.roundPts)}</span>
									</div>
									<div>|</div>
									<div className="flex gap-1">
										<em>{totCopy}</em>
										<span>{getValue(playerB?.tournamentPts)}</span>
									</div>
									<div>|</div>
									<div className="flex gap-1">
										<em>{posCopy}</em>
										<span>{getValue(playerB?.feedPosition)}</span>
									</div>
								</Exist>
							</LineupRowPlayerStats>

							<Exist when={isMobile}>
								<LineupRowPlayerStats className="flex w-full justify-end">
									<div className="flex gap-1">
										<em>{roundCopy}</em>
										<span>{getValue(playerB?.roundPts)}</span>
									</div>
									<div>|</div>
									<div className="flex gap-1">
										<em>{totCopy}</em>
										<span>{getValue(playerB?.tournamentPts)}</span>
									</div>
								</LineupRowPlayerStats>
							</Exist>
							{/*<Exist when={isMobile}>*/}
							{/*	<div className="font-semibold ml-0.5 truncate max-w-[90px]">*/}
							{/*		{playerB?.name}*/}
							{/*	</div>*/}
							{/*</Exist>*/}
						</div>
					</LineupRowPlayer>
				</Container>

				<Collapse in={isOpen}>
					{/*<PlayerNameRow>*/}
					{/*	<PlayerName*/}
					{/*		sx={{justifyContent: "flex-end"}}*/}
					{/*		className="font-semibold ml-0.5 truncate">*/}
					{/*		{playerA?.name}*/}
					{/*	</PlayerName>*/}
					{/*	<PlayerName*/}
					{/*		sx={{justifyContent: "flex-start"}}*/}
					{/*		className="font-semibold ml-0.5 truncate">*/}
					{/*		{playerB?.name}*/}
					{/*	</PlayerName>*/}
					{/*</PlayerNameRow>*/}
					<BonusesRow className="head bg-[#151B34] text-white">
						<div>{i18n.t("modal.compare.pts", "PTS")}</div>
						<div>{i18n.t("modal.compare.shot", "SHOT")}</div>
						<div />
						<div>{i18n.t("modal.compare.shot", "SHOT")}</div>
						<div>{i18n.t("modal.compare.pts", "PTS")}</div>
					</BonusesRow>

					{bonuses.map(({bonus, playerA, playerB}) => {
						const data = bonusesData[bonus];
						return (
							<BonusesRow key={bonus}>
								<div>{getValue(playerA?.pts)}</div>
								<div>{getValue(playerA?.count)}</div>
								<div>{i18n.t(data?.key, data?.defVal)}</div>
								<div>{getValue(playerB?.count)}</div>
								<div>{getValue(playerB?.pts)}</div>
							</BonusesRow>
						);
					})}
				</Collapse>
			</Wrapper>
		);
	}
);
